import React, { useEffect, useMemo } from "react";
import { Copy, MapPinned, Route, Users } from "lucide-react";
import { toast } from "sonner";
import { useData } from "../contexts/DataContext";
import { MapPanel } from "../components/MapPanel";
import { StatCard } from "../components/StatCard";
import HandoverPanel from "../components/HandoverPanel";
import { copyToClipboard, formatDateTime, getStatusLabel } from "../lib/utils";

export default function ParentDashboard() {
  const { overview, trips, childrenRecords, loadOverview, loadTrips, loadChildren } = useData();

  useEffect(() => {
    const hydrate = async () => {
      await Promise.all([loadOverview(), loadTrips(), loadChildren()]);
    };
    hydrate();
    const interval = window.setInterval(hydrate, 10000);
    return () => window.clearInterval(interval);
  }, [loadOverview, loadTrips, loadChildren]);

  const currentTrip = useMemo(
    () => overview?.current_trip || trips.find((trip) => trip.status === "in_progress") || trips[0] || null,
    [overview, trips]
  );

  const shareLink = currentTrip ? `${window.location.origin}/track/${currentTrip.tracking_token}` : "";
  const markers = currentTrip?.last_location ? [currentTrip.last_location] : [];

  const handleCopy = async () => {
    const copied = await copyToClipboard(shareLink);
    if (copied) toast.success("Tracking link copied.");
    else toast.error("Unable to copy the tracking link.");
  };

  return (
    <div className="space-y-8 animate-fade-in">
      <div className="grid gap-4 md:grid-cols-3">
        <StatCard label="Children linked" value={overview?.stats?.children || childrenRecords.length || 0} icon={Users} accent="blue" helper="Children visible to this parent account" />
        <StatCard label="Active trips" value={overview?.stats?.active_trips || 0} icon={Route} accent="emerald" helper="Live vehicle tracking for active journeys" />
        <StatCard label="Completed trips" value={overview?.stats?.completed_trips || 0} icon={MapPinned} accent="amber" helper="Journeys already finished" />
      </div>

      <div className="grid gap-6 xl:grid-cols-[1.1fr_0.9fr]">
        <div className="guardian-card bg-white p-6">
          <h2 className="font-['Manrope'] text-xl font-semibold text-[#0F172A]">Live journey tracking</h2>
          <p className="mt-1 text-sm text-[#64748B]">View the assigned vehicle location, trip status, and ETA for your child.</p>
          <div className="mt-4">
            <MapPanel markers={markers} height={420} title="Parent live tracking" />
          </div>
        </div>

        <div className="space-y-6">
          <div className="guardian-card bg-white p-6">
            <h2 className="font-['Manrope'] text-xl font-semibold text-[#0F172A]">Current trip</h2>
            {currentTrip ? (
              <div className="mt-4 space-y-3 text-sm">
                <div>
                  <p className="text-[#64748B]">Child</p>
                  <p className="font-semibold text-[#0F172A]">{currentTrip.child_name}</p>
                </div>
                <div>
                  <p className="text-[#64748B]">Status</p>
                  <p className="font-semibold text-[#0F172A]">{getStatusLabel(currentTrip.status)}</p>
                </div>
                <div>
                  <p className="text-[#64748B]">ETA</p>
                  <p className="font-semibold text-[#0F172A]">{currentTrip.eta_minutes ? `${currentTrip.eta_minutes} min` : "Pending"}</p>
                </div>
                <div>
                  <p className="text-[#64748B]">Last update</p>
                  <p className="font-semibold text-[#0F172A]">{formatDateTime(currentTrip.last_location?.timestamp || currentTrip.updated_at)}</p>
                </div>
              </div>
            ) : (
              <div className="mt-4 rounded-2xl border border-dashed border-[#CBD5E1] p-4 text-sm text-[#64748B]">No journey data available yet.</div>
            )}
          </div>

          <div className="guardian-card bg-white p-6">
            <h2 className="font-['Manrope'] text-xl font-semibold text-[#0F172A]">Share tracking link</h2>
            <p className="mt-1 text-sm text-[#64748B]">Token-based link for trusted contacts to follow the live trip.</p>
            <div className="mt-4 rounded-2xl border border-[#E2E8F0] bg-[#F8FAFC] p-4 text-sm text-[#475569] break-all">
              {shareLink || "No share link is available until a trip exists."}
            </div>
            <button
              onClick={handleCopy}
              disabled={!shareLink}
              className="mt-4 inline-flex items-center gap-2 rounded-xl bg-[#163D6B] px-4 py-3 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Copy size={16} />
              Copy share link
            </button>
          </div>
        </div>
      </div>

      {currentTrip && (
        <HandoverPanel trip={currentTrip} role="parent" onRefresh={loadTrips} />
      )}
    </div>
  );
}
